import { Color, Spacing } from '@persona-id/design-tokens';
import { useCallback, useState } from 'react';
import styled from 'styled-components';

import JsonSchemaSelect, { Option } from 'dashboard/components/JsonSchemaSelect';
import MaximizableCodeEditor from 'dashboard/components/MaximizableCodeEditor';
import ReactSelect from 'dashboard/components/ReactSelect';
import {
  KeyValueModuleDisplayFormat,
  ModuleKeyValue,
  stringifyObjectValues,
} from 'dashboard/modules/KeyValue';
import Input from 'design-system/components/Input';
import Spacer from 'design-system/components/Spacer';
import { Heading, Label, Paragraph } from 'design-system/components/Text';
import { convertFieldSchemasToJsonSchema, FieldSchema, FieldSchemaType } from 'lib/fields';

const DISPLAY_FORMAT_OPTIONS = [
  {
    label: 'Grid',
    value: KeyValueModuleDisplayFormat.Grid,
  },
  {
    label: 'Table',
    value: KeyValueModuleDisplayFormat.Table,
  },
];

const Container = styled('div')`
  display: flex;
  flex-direction: column;
  padding: ${Spacing[4]};
`;

const FormSectionContainer = styled('div')`
  padding: ${Spacing[5]} 0px;
`;

const Divider = styled('div')`
  border-top: 1px solid ${({ theme }) => theme.color.background.secondary};
`;

const ErrorContainer = styled(Paragraph)`
  margin-top: ${Spacing[2]};
  color: ${Color.CoreRed800};
`;

export interface KeyValuePanelProps {
  module: ModuleKeyValue;
  fieldSchema: FieldSchema[];
  updateModule: (module: ModuleKeyValue) => void;
}

const KeyValuePanel = ({ module, fieldSchema, updateModule }: KeyValuePanelProps) => {
  const initialPreviewData = JSON.stringify(module?.['preview-data'], null, 2);
  const [inputPreviewData, setinputPreviewData] = useState(initialPreviewData);
  const [inputDisplayFormat, setInputDisplayFormat] = useState(module['display-format']);
  const [errorMsg, setErrorMsg] = useState(null);
  const [inputValue, setInputValue] = useState<string | string[]>(
    module?.['field-key'].split('-').join('_'),
  ); // Need to have keys in kebab case

  const jsonSchema = convertFieldSchemasToJsonSchema(
    fieldSchema.filter((f) => f.type === FieldSchemaType.Json),
  );

  const handleUpdateFieldKey = (option: Option) => {
    const dasherizedKey = option.value.split('_').join('-');
    updateModule({
      ...module,
      'field-key': dasherizedKey,
    });
  };

  const handlePreviewDataUpdate = useCallback(
    (previewJson: string) => {
      setinputPreviewData(previewJson);
      try {
        const parsedJson = JSON.parse(previewJson);
        setErrorMsg(null);
        updateModule({
          ...module,
          'preview-data': stringifyObjectValues(parsedJson),
        });
      } catch (e) {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        setErrorMsg(`JSON ParseError: ${e.message}`);
      }
    },
    [updateModule],
  );

  const handleUpdateDisplayFormat = (displayFormat: KeyValueModuleDisplayFormat) => {
    setInputDisplayFormat(displayFormat);
    updateModule({
      ...module,
      'display-format': displayFormat,
    });
  };

  const handleUpdateTitle = (title: string) => {
    updateModule({
      ...module,
      title,
    });
  };

  const handleUpdateKeyLabel = (keyLabel: string) => {
    updateModule({
      ...module,
      'key-label': keyLabel,
    });
  };

  const handleUpdateValueLabel = (valueLabel: string) => {
    updateModule({
      ...module,
      'value-label': valueLabel,
    });
  };

  return (
    <Container>
      <FormSectionContainer>
        <Heading size='sm'>Settings</Heading>
        <Spacer height={3} />
        <Label size='md' weight='bold'>
          Title
        </Label>
        <Input
          id='key-value__input--title'
          type='text'
          onChange={(e) => {
            handleUpdateTitle(e.target.value);
          }}
          placeholder='Enter title'
          value={module.title}
        />
        <Divider />
      </FormSectionContainer>
      <FormSectionContainer>
        <Heading size='sm'>Data Configuration</Heading>
        <Spacer height={3} />
        <Label size='md' weight='bold'>
          Field Key
        </Label>
        <JsonSchemaSelect
          jsonSchema={jsonSchema}
          inputValue={inputValue}
          tokensToDisplayValue={{}}
          onValueChange={setInputValue}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          onFieldSelect={handleUpdateFieldKey}
        />
        <Spacer height={1} />
        <Label size='md' weight='bold'>
          Preview Data
        </Label>
        <MaximizableCodeEditor
          data-test='key-value__preview-data-editor'
          code={inputPreviewData}
          language='json'
          modalTitle='Preview Data Editor'
          onCodeUpdate={handlePreviewDataUpdate}
        />
        {errorMsg && <ErrorContainer>{errorMsg}</ErrorContainer>}
        <Divider />
      </FormSectionContainer>
      <FormSectionContainer>
        <Heading size='sm'>Display Configuration</Heading>
        <Spacer height={3} />
        <Label size='md' weight='bold'>
          Display Format
        </Label>
        <ReactSelect
          options={DISPLAY_FORMAT_OPTIONS}
          value={inputDisplayFormat}
          onChange={handleUpdateDisplayFormat}
          portal
        />
        <Spacer height={1} />
        {module['display-format'] === 'table' && (
          <>
            <Label size='md' weight='bold'>
              Key Column Label
            </Label>
            <Input
              id='key-value__input--key-label'
              type='text'
              onChange={(e) => {
                handleUpdateKeyLabel(e.target.value);
              }}
              placeholder='Enter key column label'
              value={module['key-label']}
            />
            <Spacer height={1} />
            <Label size='md' weight='bold'>
              Value Column Label
            </Label>
            <Input
              id='key-value__input--value-label'
              type='text'
              onChange={(e) => {
                handleUpdateValueLabel(e.target.value);
              }}
              placeholder='Enter value column label'
              value={module['value-label']}
            />
          </>
        )}
      </FormSectionContainer>
    </Container>
  );
};

export default KeyValuePanel;
